/**
 * 書式のエディタの紙（左側）。packages/paper の描画（mode=edit）をそのまま入れ、外側を transform: scale で縮める。
 *   ・部品と明細表の位置は描き手が決めた位置（paperGeometry。#217：明細表はページの下まで空行で埋め、明細の下の部品はその下）
 *   ・枠（#217 ■4。上の帯の「枠」）：全部の部品の外枠を薄い点線、明細表は全体の枠と列の境。明細表を選ぶと見出しの上に列ごとの幅（mm）
 *   ・重なり：両方の枠を朱、重なった範囲に朱の斜線。乗せると「名称 × 規格」のように相手を出す
 *   ・ドラッグで動かす／角と辺の四角で大きさを変える（動かしている間は DOM を直接ずらし、離したときに書式に書く）
 *   ・組（group）は「組で動かす」が入っていると一緒に動く。dock の部品は表の下からの距離（dy）が変わる
 *   ・文字の部品はダブルクリックでその場で直す（Enter で決定、Shift+Enter で改行、Esc で戻す）
 *   ・定規（mm）、紙の端 5mm（朱の点線）、余白（藍の点線）、方眼、通し番号の帯（#223：下の中央・幅 140mm・下端から 5〜11mm に薄い点線と名前。
 *     送付状の FAX の型で全ページに「n／N　書類名 番号」を重ねる所＝部品が重なると重なりの注意で帯も朱）。印刷には出ない
 *   ・ドラッグだけの操作にしない：同じことは右の「部品」タブの数字と矢印キーでもできる（docs/10 決めごと5）
 */
import { THROUGH_BAND_NAME, throughBandBox, type PaperData, type PaperEl, type PaperLayout } from "@genbaryoku/contract";
import { FONT_CSS, THROUGH_BAND_ID, paperGeometry, renderPages, type LayoutWarning } from "@genbaryoku/paper";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { currentLabels } from "../../labels.js";
import { LightArea, useColorModeNow } from "../../components/LightArea.jsx";
import { PX_PER_MM, snapTo, type Sel } from "./editorState.js";

type Props = {
  layout: PaperLayout;
  data: PaperData;
  sel: Sel;
  onSelect: (sel: Sel) => void;
  /** ドラッグの結果（動かした・大きさを変えた）を書式に書く */
  onChange: (next: PaperLayout) => void;
  zoom: "fit" | "50" | "75" | "100" | "150";
  snap: number;
  grid: boolean;
  groupMove: boolean;
  /** 文字をその場で直す（id）。null で終わり */
  editing: string | null;
  onEditStart: (id: string) => void;
  onEditEnd: (id: string, text: string | null) => void;
  /** 画像の id → URL（046。API の /paper/images/:id・/company/logo|seal） */
  assets: Record<string, string>;
  /** 朱の点線で囲む部品（「注意 n」の一覧を開いているあいだ。部品の id・明細表は "table"。#171） */
  marks?: string[] | undefined;
  /** 全部の部品の外枠を点線で出す（#217 ■4。上の帯の「枠」） */
  frames: boolean;
  /** 注意（#217 ■4）：重なりは両方の枠を朱・重なった範囲に斜線 */
  warnings: LayoutWarning[];
};

/** dock：明細表の下（table）か備考の下（end、#233 2d）に付く部品＝y は基準からの距離（dy） */
type Target = { id: string; x: number; y: number; w: number; h: number; table: boolean; dock: false | "table" | "end" };
type Drag = { dir: string | null; sx: number; sy: number; sc: number; targets: Target[]; moved: boolean };

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

export function PaperCanvas({ layout, data, sel, onSelect, onChange, zoom, snap, grid, groupMove, editing, onEditStart, onEditEnd, assets, marks, frames, warnings }: Props) {
  const outer = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(800);
  const drag = useRef<Drag | null>(null);
  const downOnEl = useRef(false);
  const { w: pw, h: ph } = layout.page;

  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setAvail(el.clientWidth));
    ro.observe(el);
    setAvail(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const sc = useMemo(() => {
    if (zoom === "fit") return Math.min(1.5, (avail - 24 - 16) / (pw * PX_PER_MM));
    return Number(zoom) / 100;
  }, [zoom, avail, pw]);

  const html = useMemo(() => renderPages(layout, data, { mode: "edit", assets, labels: currentLabels() }).pages[0] ?? "", [layout, data, assets]);
  /** 描き手が決めた1ページ目の位置（明細表の高さ・明細の下の部品の位置は中身と「空行で埋める」で決まる） */
  const geom = useMemo(() => paperGeometry(layout, data), [layout, data]);
  /** 通し番号の帯（#223）：送付状の FAX の型で全ページの下の中央に重ねる所 */
  const band = throughBandBox(layout.page);
  const boxOf = (id: string) => (id === "table" ? geom.table : id === THROUGH_BAND_ID ? band : geom.els[id]);

  // 定規
  const mode = useColorModeNow();
  const rulerTop = useRef<HTMLCanvasElement>(null);
  const rulerLeft = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    const W = pw * PX_PER_MM * sc;
    const H = ph * PX_PER_MM * sc;
    const draw = (cv: HTMLCanvasElement | null, len: number, horiz: boolean) => {
      if (!cv) return;
      cv.width = (horiz ? W : 24) * dpr;
      cv.height = (horiz ? 24 : H) * dpr;
      cv.style.width = `${horiz ? W : 24}px`;
      cv.style.height = `${horiz ? 24 : H}px`;
      const x = cv.getContext("2d");
      if (!x) return;
      x.scale(dpr, dpr);
      // 定規の色は明るさに合わせる（#191 の2回目：見出しの地・太罫・薄墨の変数。ダークでは暗い定規）
      const cs = getComputedStyle(outer.current ?? document.documentElement);
      x.fillStyle = cs.getPropertyValue("--head").trim() || "#EFEEE9";
      x.fillRect(0, 0, horiz ? W : 24, horiz ? 24 : H);
      x.strokeStyle = cs.getPropertyValue("--bold").trim() || "#8C8880";
      x.fillStyle = cs.getPropertyValue("--ink2").trim() || "#55534C";
      x.font = "10px sans-serif";
      x.lineWidth = 1;
      for (let mm = 0; mm <= len; mm += 1) {
        const p = mm * PX_PER_MM * sc;
        const big = mm % 10 === 0;
        const mid = mm % 5 === 0;
        if (!big && !mid && sc * PX_PER_MM < 2.5) continue;
        const l = big ? 10 : mid ? 6 : 3;
        x.beginPath();
        if (horiz) { x.moveTo(p + 0.5, 24); x.lineTo(p + 0.5, 24 - l); } else { x.moveTo(24, p + 0.5); x.lineTo(24 - l, p + 0.5); }
        x.stroke();
        if (big && mm > 0) {
          if (horiz) x.fillText(String(mm), p + 2, 10);
          else { x.save(); x.translate(10, p - 2); x.rotate(-Math.PI / 2); x.fillText(String(mm), 0, 0); x.restore(); }
        }
      }
    };
    draw(rulerTop.current, pw, true);
    draw(rulerLeft.current, ph, false);
  }, [pw, ph, sc, mode]);

  /* ---- ドラッグ ---- */
  const nodeOf = (id: string): HTMLElement | null => wrap.current?.querySelector(`[data-id="${CSS.escape(id)}"]`) ?? null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (editing) return;
    const t = e.target as HTMLElement;
    const hd = t.closest<HTMLElement>(".pe-hd");
    const n = t.closest<HTMLElement>("[data-id]");
    downOnEl.current = !!n;
    if (!n) return;
    const id = n.dataset.id!;
    if (id !== sel) onSelect(id);
    const tbl = layout.table;
    const targets: Target[] = [];
    if (id === "table") targets.push({ id, x: tbl.x, y: tbl.y, w: tbl.w, h: geom.table.h, table: true, dock: false });
    else {
      const el = layout.els.find((x) => x.id === id);
      if (!el) return;
      const grp = groupMove && el.group && !hd ? layout.els.filter((x) => x.group === el.group) : [el];
      for (const o of grp) targets.push({ id: o.id, x: o.x, y: o.dock ? (o.dy ?? 0) : o.y, w: o.w, h: o.h, table: false, dock: o.dock ?? false });
    }
    drag.current = { dir: hd?.dataset.dir ?? null, sx: e.clientX, sy: e.clientY, sc, targets, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    // preventDefault は入力欄のフォーカスを外さないので、矢印キーが紙に効くように自分で外す
    (document.activeElement as HTMLElement | null)?.blur?.();
    e.preventDefault();
  };

  /** 動かしている間の位置（mm）。離したときに書式へ */
  const compute = (d: Drag, dx: number, dy: number): Map<string, { x: number; y: number; w: number; h: number }> => {
    const out = new Map<string, { x: number; y: number; w: number; h: number }>();
    for (const tg of d.targets) {
      let { x, y, w, h } = tg;
      if (!d.dir) { x = snapTo(tg.x + dx, snap); y = snapTo(tg.y + dy, snap); }
      else {
        const dr = d.dir;
        if (dr.includes("e")) w = Math.max(1, tg.w + dx);
        if (dr.includes("s")) h = Math.max(1, tg.h + dy);
        if (dr.includes("w")) { x = tg.x + dx; w = Math.max(1, tg.w - dx); }
        if (dr.includes("n")) { y = tg.y + dy; h = Math.max(1, tg.h - dy); }
        x = snapTo(x, snap); y = snapTo(y, snap); w = snapTo(w, snap); h = snapTo(h, snap);
        if (tg.table) h = tg.h; // 表の高さは行数で決まる
      }
      out.set(tg.id, { x, y, w, h });
    }
    return out;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) {
      // 重なりに乗せたら相手を出す（「名称 × 規格」）
      const r = wrap.current?.getBoundingClientRect();
      if (!r || !overlaps.length) { if (tip) setTip(null); return; }
      const mx = (e.clientX - r.left) / sc / PX_PER_MM;
      const my = (e.clientY - r.top) / sc / PX_PER_MM;
      const inside = (b: { x: number; y: number; w: number; h: number } | undefined) => !!b && mx >= b.x && mx <= b.x + b.w && my >= b.y && my <= b.y + b.h;
      const hit = overlaps.find((w) => inside(w.rect)) ?? overlaps.find((w) => inside(w.target ? boxOf(w.target) : undefined) || inside(w.other ? boxOf(w.other) : undefined));
      const next = hit ? { x: mx, y: my, text: `${hit.name} × ${hit.otherName ?? ""}` } : null;
      if (next?.text !== tip?.text || (next && tip && (Math.abs(next.x - tip.x) > 2 || Math.abs(next.y - tip.y) > 2))) setTip(next);
      return;
    }
    const dx = (e.clientX - d.sx) / d.sc / PX_PER_MM;
    const dy = (e.clientY - d.sy) / d.sc / PX_PER_MM;
    if (!d.moved && Math.abs(dx) < 0.3 && Math.abs(dy) < 0.3) return;
    d.moved = true;
    const pos = compute(d, dx, dy);
    const base = geom.dockBase;
    const baseOf = (tg: Target) => (tg.dock === "end" ? (geom.endBase ?? base) : base);
    for (const tg of d.targets) {
      const p = pos.get(tg.id)!;
      const n = nodeOf(tg.id);
      if (!n) continue;
      const el = layout.els.find((x) => x.id === tg.id);
      const isV = el?.type === "line" && el.dir === "v";
      const isH = el?.type === "line" && el.dir === "h";
      n.style.left = `${p.x}mm`;
      n.style.top = `${tg.dock ? baseOf(tg) + p.y : p.y}mm`;
      if (!isV) n.style.width = `${Math.max(p.w, 0.1)}mm`;
      if (!isH && !tg.table) n.style.height = `${Math.max(p.h, 0.1)}mm`;
      if (isV) n.style.height = `${p.h}mm`;
      if (isH) n.style.width = `${p.w}mm`;
    }
    // 表を動かしたら、表の下に付いている部品も一緒に
    if (d.targets[0]?.table) {
      const p = pos.get("table")!;
      const shift = p.y - layout.table.y;
      for (const el of layout.els) {
        if (el.dock !== "table") continue;
        const n = nodeOf(el.id);
        if (n) n.style.top = `${base + (el.dy ?? 0) + shift}mm`;
      }
    }
    // 選択の枠
    const box = wrap.current?.querySelector<HTMLElement>(".pe-selbox");
    if (box && sel) {
      const p = pos.get(sel);
      if (p) {
        box.style.left = `${p.x}mm`;
        const st = d.targets.find((t) => t.id === sel);
        box.style.top = `${st?.dock ? baseOf(st) + p.y : p.y}mm`;
        box.style.width = `${Math.max(p.w, 0.1)}mm`;
        box.style.height = `${Math.max(p.h, 0.1)}mm`;
      }
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.moved) return;
    const dx = (e.clientX - d.sx) / d.sc / PX_PER_MM;
    const dy = (e.clientY - d.sy) / d.sc / PX_PER_MM;
    const pos = compute(d, dx, dy);
    let next = layout;
    for (const tg of d.targets) {
      const p = pos.get(tg.id)!;
      if (tg.table) next = { ...next, table: { ...next.table, x: p.x, y: p.y, w: p.w } };
      else
        next = {
          ...next,
          els: next.els.map((el) => {
            if (el.id !== tg.id) return el;
            const patch: Partial<PaperEl> = { x: p.x };
            if (el.dock) patch.dy = p.y; else patch.y = p.y;
            if (d.dir) { if (!(el.type === "line" && el.dir === "v")) patch.w = p.w; if (!(el.type === "line" && el.dir === "h")) patch.h = p.h; }
            return { ...el, ...patch } as PaperEl;
          }),
        };
    }
    onChange(next);
  };

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (downOnEl.current) { downOnEl.current = false; return; }
    if (!(e.target as HTMLElement).closest("[data-id]") && !editing) onSelect(null);
  };
  const onDblClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editing) return;
    const n = (e.target as HTMLElement).closest<HTMLElement>("[data-id]");
    if (!n) return;
    const el = layout.els.find((x) => x.id === n.dataset.id);
    if (el?.type === "text") onEditStart(el.id);
  };

  /* ---- 選択の枠と、その場の文字の直し ---- */
  const selBox = useMemo(() => (sel ? (boxOf(sel) ?? null) : null), [sel, geom]);
  const markBoxes = useMemo(() => (marks ?? []).flatMap((m) => { const b = boxOf(m); return b ? [{ id: m, ...b }] : []; }), [marks, geom]);
  /** 重なり（#217 ■4）：両方の枠を朱、重なった範囲に斜線 */
  const overlaps = useMemo(() => warnings.filter((w) => w.kind === "overlap"), [warnings]);
  const overlapIds = useMemo(() => [...new Set(overlaps.flatMap((w) => [w.target, w.other]).filter((x): x is string => !!x))], [overlaps]);
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const editEl = editing ? layout.els.find((x): x is Extract<PaperEl, { type: "text" }> => x.id === editing && x.type === "text") : undefined;
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (editEl && taRef.current) { taRef.current.focus(); taRef.current.select(); }
  }, [editEl?.id]);

  const [mt, mr, mb, ml] = layout.page.margin;
  const hs = 8 / sc; // 四角の大きさ（画面上で 8px）

  return (
    <div ref={outer} className="pe-canvas">
      <div className="pe-rulerbox" style={{ width: pw * PX_PER_MM * sc + 24, height: ph * PX_PER_MM * sc + 24 }}>
        <canvas ref={rulerTop} className="pe-ruler-top" aria-hidden="true" />
        <canvas ref={rulerLeft} className="pe-ruler-left" aria-hidden="true" />
        <div className="pe-fit" style={{ width: pw * PX_PER_MM * sc, height: ph * PX_PER_MM * sc }}>
          {/* 紙そのものはダークでもライト（#191 の2回目、docs/77 §8.4 ヨ）。周りの机・定規・枠はダーク */}
          <LightArea>
          <div
            ref={wrap}
            className={"pe-wrap" + (layout.page.preprinted ? " pre" : "")}
            data-testid="paper-canvas"
            style={{ width: `${pw}mm`, height: `${ph}mm`, transform: `scale(${sc})` }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={onClick}
            onDoubleClick={onDblClick}
          >
            {grid ? <div className="pe-grid" style={{ backgroundSize: "5mm 5mm" }} /> : null}
            <div className="pe-safe" style={{ left: "5mm", top: "5mm", width: `${pw - 10}mm`, height: `${ph - 10}mm` }} />
            <div className="pe-margin" style={{ left: `${ml}mm`, top: `${mt}mm`, width: `${pw - ml - mr}mm`, height: `${ph - mt - mb}mm` }} />
            <div className="pe-through" aria-hidden="true" data-testid="pe-through" style={{ left: `${band.x}mm`, top: `${band.y}mm`, width: `${band.w}mm`, height: `${band.h}mm` }}>
              <span>{THROUGH_BAND_NAME}</span>
            </div>
            <div className="pe-paper" dangerouslySetInnerHTML={{ __html: html }} />
            {frames ? (
              <div className="pe-frames" aria-hidden="true" data-testid="pe-frames">
                {layout.els.map((el) => {
                  const b = geom.els[el.id];
                  if (!b || el.type === "line" || (el.type === "image" && el.guide)) return null;
                  return <div key={el.id} className="pe-frame" data-frame={el.id} style={{ left: `${b.x}mm`, top: `${b.y}mm`, width: `${Math.max(b.w, 0.1)}mm`, height: `${Math.max(b.h, 0.1)}mm`, outlineWidth: 1 / sc }} />;
                })}
                <div className="pe-frame is-table" data-frame="table" style={{ left: `${geom.table.x}mm`, top: `${geom.table.y}mm`, width: `${geom.table.w}mm`, height: `${geom.table.h}mm`, outlineWidth: 1 / sc }} />
                {geom.cols.slice(1).map((c) => (
                  <div key={c.id} className="pe-colline" style={{ left: `${c.x}mm`, top: `${geom.table.y}mm`, height: `${geom.table.h}mm`, borderLeftWidth: 1 / sc }} />
                ))}
                {geom.boxes.map((b, i) => (
                  <div key={`bx${i}`} className="pe-frame" style={{ left: `${b.x}mm`, top: `${b.y}mm`, width: `${b.w}mm`, height: `${b.h}mm`, outlineWidth: 1 / sc }} />
                ))}
              </div>
            ) : null}
            {sel === "table" ? (
              <div className="pe-colws" aria-hidden="true" data-testid="pe-colws">
                {geom.cols.map((c) => (
                  <span key={c.id} className="pe-colw" style={{ left: `${c.x}mm`, top: `${geom.table.y - 4.2}mm`, width: `${c.w}mm`, fontSize: `${11 / sc / PX_PER_MM}mm` }}>{Math.round(c.w * 10) / 10}</span>
                ))}
              </div>
            ) : null}
            {overlapIds.map((id) => {
              const b = boxOf(id);
              return b ? <div key={`ov-${id}`} className="pe-ovl" data-ovl={id} style={{ left: `${b.x}mm`, top: `${b.y}mm`, width: `${Math.max(b.w, 0.1)}mm`, height: `${Math.max(b.h, 0.1)}mm`, outlineWidth: 2 / sc }} /> : null;
            })}
            {overlaps.map((w, i) => (w.rect ? <div key={`h${i}`} className="pe-hatch" data-testid="pe-hatch" style={{ left: `${w.rect.x}mm`, top: `${w.rect.y}mm`, width: `${Math.max(w.rect.w, 0.3)}mm`, height: `${Math.max(w.rect.h, 0.3)}mm` }} /> : null))}
            {tip ? <div className="pe-ovtip" data-testid="pe-ovtip" style={{ left: `${tip.x + 2}mm`, top: `${tip.y + 2}mm`, fontSize: `${12 / sc / PX_PER_MM}mm` }}>{tip.text}</div> : null}
            {markBoxes.map((b) => (
              <div key={b.id} className="pe-markbox" data-mark={b.id} data-testid="pe-mark" style={{ left: `${b.x}mm`, top: `${b.y}mm`, width: `${Math.max(b.w, 0.1)}mm`, height: `${Math.max(b.h, 0.1)}mm`, outlineWidth: 2 / sc, outlineOffset: 2 / sc }} />
            ))}
            {selBox ? (
              <div className="pe-selbox" style={{ left: `${selBox.x}mm`, top: `${selBox.y}mm`, width: `${Math.max(selBox.w, 0.1)}mm`, height: `${Math.max(selBox.h, 0.1)}mm`, outlineWidth: 2 / sc }}>
                {HANDLES.map((d) => (
                  <i key={d} className="pe-hd" data-dir={d} style={{ width: hs, height: hs, margin: -hs / 2 }} />
                ))}
              </div>
            ) : null}
            {editEl ? (
              <textarea
                ref={taRef}
                className="pe-inline"
                aria-label="文言"
                defaultValue={editEl.text}
                style={{
                  left: `${editEl.x}mm`, top: `${geom.els[editEl.id]?.y ?? editEl.y}mm`, width: `${editEl.w}mm`, height: `${editEl.h}mm`,
                  fontFamily: FONT_CSS[editEl.font], fontSize: `${editEl.size}pt`, fontWeight: editEl.bold ? 700 : 400, color: editEl.color, textAlign: editEl.align, outlineWidth: 2 / sc,
                }}
                onBlur={(ev) => onEditEnd(editEl.id, ev.currentTarget.value)}
                onKeyDown={(ev) => {
                  ev.stopPropagation();
                  if (ev.key === "Escape") { ev.preventDefault(); onEditEnd(editEl.id, null); }
                  else if (ev.key === "Enter" && !ev.shiftKey && !ev.nativeEvent.isComposing) { ev.preventDefault(); onEditEnd(editEl.id, ev.currentTarget.value); }
                }}
                onPointerDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => ev.stopPropagation()}
              />
            ) : null}
          </div>
          </LightArea>
        </div>
      </div>
    </div>
  );
}
